import { toast } from 'sonner';
import { createLogger } from '@/lib/logging';

const logger = createLogger('VersionCheck');

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Identifies the running bundle itself, so a tab that loaded a stale build
// still notices, and a failed first fetch doesn't disable the check.
const loadedVersion = __BUILD_ID__;
let lastCheck = 0;
let notified = false;

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const info = await res.json();
    return info.commitSha || info.buildTimestamp || null;
  } catch {
    return null;
  }
}

async function check() {
  if (notified || Date.now() - lastCheck < CHECK_INTERVAL_MS) return;
  lastCheck = Date.now();

  const deployed = await fetchDeployedVersion();
  if (!deployed || loadedVersion === 'unknown' || deployed === loadedVersion) return;

  // A tab left open across a deploy keeps running the old bundle against the
  // new server. Ask rather than auto-reload so an in-progress upload isn't lost.
  notified = true;
  logger.info('New version deployed', { loadedVersion, deployed });
  toast('A new version of verif.ai is available', {
    description: 'Reload to get the latest fixes.',
    duration: Infinity,
    action: { label: 'Reload', onClick: () => window.location.reload() },
  });
}

/**
 * Compares this bundle's build ID with the deployed /version.json at startup,
 * when the tab regains focus and every 5 minutes while visible, and offers a
 * reload when they differ.
 */
export async function startVersionCheck() {
  if (import.meta.env.DEV) return;

  // Check once at startup too: the page may have come from a stale cache.
  await check();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  window.addEventListener('focus', () => void check());
  // A tab that stays in front never fires focus events, so check on a timer too.
  // Tick a little faster than the throttle so a tick never lands just inside it.
  setInterval(() => { if (document.visibilityState === 'visible') void check(); }, CHECK_INTERVAL_MS - 60_000);
}
